import { useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { WelcomeOfferStatus } from "@/components/WelcomeOfferStatus"
import { ReferringAffiliateCard } from "@/components/ReferringAffiliateCard"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { PageHeader } from "@/components/PageHeader"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Alert } from "@/components/Alert/Alert"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/Dialog/Dialog"
import { Input } from "@/components/Form/Input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/Form/Select"
import { useUserDetail, useSuspendUser, useUnsuspendUser, useRevokeUserSessions, useUserFeatureFlags, useUpdateUserFeatureFlag } from "@/hooks/useUserDetail"
import {
  useDeleteUser,
  describeDeleteUserError,
} from "@/hooks/useUsers"
import { useAuth } from "../contexts/use-auth"
import { useIndividualLimits } from "@/hooks/useLimits"
import { UsageChart } from "@/components/Charts/UsageChart"
import { StorageProgressBar } from "@/components/Charts/StorageProgressBar"
import { Switch } from "@/components/Form/Switch"
import { useToast } from "@/components/Toast"
import { client, APIError } from "@/api"
import { 
  UserIcon, 
  UserGroupIcon,
  VideoCameraIcon,
  FilmIcon,
  CalendarIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ArrowLeftIcon,
  PencilIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  Squares2X2Icon,
  TrashIcon,
  GiftIcon,
} from "@heroicons/react/24/outline"
import { formatDistanceToNow } from "date-fns"
import { formatSignupSource } from "@/lib/signup-source"

// SQLite CURRENT_TIMESTAMP produces "YYYY-MM-DD HH:MM:SS" (UTC, no Z), which
// JavaScript's Date constructor parses as LOCAL time. Normalize to ISO-with-Z
// so it's interpreted as UTC. Safe to call on already-ISO strings.
function parseDbTimestamp(value: string): Date {
  if (value.includes("T") || value.endsWith("Z")) return new Date(value)
  return new Date(value.replace(" ", "T") + "Z")
}

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user: currentUser } = useAuth()
  const { data, isLoading, error, refetch } = useUserDetail(userId!)
  const { data: individualLimitsData } = useIndividualLimits(userId!)
  const { data: featureFlagsData } = useUserFeatureFlags(userId!)
  const updateFeatureFlag = useUpdateUserFeatureFlag()
  const suspendUser = useSuspendUser()
  const unsuspendUser = useUnsuspendUser()
  const revokeUserSessions = useRevokeUserSessions()
  const deleteUser = useDeleteUser()

  const [showSuspendDialog, setShowSuspendDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showRevokeSessionsDialog, setShowRevokeSessionsDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState("")
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [suspendForm, setSuspendForm] = useState({
    reason: "",
    duration: "permanent",
    customDays: "7",
  })
  
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    is_superuser: false,
  })

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-64" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const { user, teams, stats, welcome_offer: welcomeOffer } = data
  const signupSource = formatSignupSource(user)
  const featureFlags = featureFlagsData?.flags ?? []

  // The backend rejects deleting a superuser or the acting admin's own account
  // (both 403). Don't render an action that always fails — gate it here too.
  const isSelf = !!currentUser && currentUser.public_id === user.public_id
  const canDelete = !user.is_superuser && !isSelf

  const openDeleteDialog = () => {
    setDeleteConfirmText("")
    setDeleteError(null)
    setShowDeleteDialog(true)
  }

  const handleFeatureFlagToggle = async (flagKey: string, enabled: boolean) => {
    try {
      await updateFeatureFlag.mutateAsync({
        userId: userId!,
        flagKey,
        enabled,
      })

      toast({
        title: `Feature flag ${enabled ? "enabled" : "disabled"}`,
        description: "Changes apply immediately for this user.",
      })
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to update the feature flag. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleSuspend = async () => {
    try {
      let expiresAt = undefined
      if (suspendForm.duration !== "permanent") {
        const days = suspendForm.duration === "custom" 
          ? parseInt(suspendForm.customDays) 
          : parseInt(suspendForm.duration)
        const expiry = new Date()
        expiry.setDate(expiry.getDate() + days)
        expiresAt = expiry.toISOString()
      }

      await suspendUser.mutateAsync({
        userId: userId!,
        reason: suspendForm.reason,
        expiresAt,
      })

      toast({
        title: "User suspended",
        description: "The user has been suspended successfully.",
      })
      setShowSuspendDialog(false)
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to suspend user. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleUnsuspend = async () => {
    try {
      await unsuspendUser.mutateAsync({
        userId: userId!,
      })

      toast({
        title: "User unsuspended",
        description: "The user has been unsuspended successfully.",
      })
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to unsuspend user. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleRevokeSessions = async () => {
    try {
      await revokeUserSessions.mutateAsync({
        userId: userId!,
      })

      toast({
        title: "Sessions revoked",
        description: "All user sessions have been revoked. The user will need to log in again.",
      })
      setShowRevokeSessionsDialog(false)
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to revoke sessions. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleEdit = async () => {
    try {
      const response = await client.admin.users[":publicId"].$put({
        param: { publicId: userId! },
        json: editForm,
      })

      if (!response.ok) {
        const error = await response.json() as any
        throw new APIError(
          response.status,
          error.code || "USER_UPDATE_FAILED",
          new Error(error.message || error.error || "Failed to update user")
        )
      }

      toast({
        title: "User updated",
        description: "The user details have been updated successfully.",
      })
      setShowEditDialog(false)
      refetch()
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof APIError ? error.originalError.message : "Failed to update user. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async () => {
    setDeleteError(null)
    try {
      await deleteUser.mutateAsync({ userId: userId! })

      toast({
        title: "User deleted",
        description: `${user.name} has been deleted. Team content was reassigned to "Deleted User".`,
      })
      // The user no longer exists; leave the (now-404) detail page.
      setShowDeleteDialog(false)
      navigate("/users")
    } catch (error) {
      // 403/404/409/501: surface the server message / translated blocker in the
      // modal and keep it open so the admin can read it and close it.
      setDeleteError(describeDeleteUserError(error))
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate("/users")}
                className="p-2 hover:bg-surface rounded-md transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <span>User Details</span>
            </div>
          }
          actions={
            <div className="flex items-center space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditForm({
                    name: user.name,
                    email: user.email,
                    is_superuser: user.is_superuser,
                  })
                  setShowEditDialog(true)
                }}
              >
                <PencilIcon className="h-4 w-4" />
                Edit
              </Button>
              {user.suspended_at ? (
                <Button
                  onClick={handleUnsuspend}
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Unsuspend
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={() => setShowSuspendDialog(true)}
                >
                  <NoSymbolIcon className="h-4 w-4" />
                  Suspend
                </Button>
              )}
            </div>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* User Info Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">User Information</h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <UserIcon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-medium">{user.name}</h3>
                    <p className="text-secondary">{user.email}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.email_verified 
                          ? "bg-green-100 text-green-800" 
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {user.email_verified ? (
                          <>
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Verified
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Unverified
                          </>
                        )}
                      </span>
                      {user.is_superuser && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          <ShieldCheckIcon className="h-3 w-3 mr-1" />
                          Admin
                        </span>
                      )}
                      {user.suspended_at && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                          <ShieldExclamationIcon className="h-3 w-3 mr-1" />
                          Suspended
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {user.suspended_at && (
                  <Alert variant="destructive">
                    <ShieldExclamationIcon className="h-4 w-4" />
                    <div>
                      <p className="font-medium">Account Suspended</p>
                      <p className="text-sm mt-1">
                        Reason: {user.suspension_reason || "No reason provided"}
                      </p>
                      {user.suspension_expires_at && (
                        <p className="text-sm">
                          Expires: {new Date(user.suspension_expires_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </Alert>
                )}

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-sm text-secondary">User ID</p>
                    <p className="font-mono text-sm">{user.public_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary">Joined</p>
                    <p className="text-sm">
                      {parseDbTimestamp(user.created_at).toLocaleDateString()}
                      <span className="text-secondary ml-1">
                        ({formatDistanceToNow(parseDbTimestamp(user.created_at), { addSuffix: true })})
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary">Signup method</p>
                    <p className="text-sm capitalize">
                      {user.signup_auth_provider ?? <span className="text-secondary">unknown</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary">Last login</p>
                    <p className="text-sm">
                      {user.last_login ? (
                        <>
                          <span className="capitalize">{user.last_login_auth_provider ?? "unknown"}</span>
                          <span className="text-secondary ml-1">
                            ({formatDistanceToNow(parseDbTimestamp(user.last_login), { addSuffix: true })})
                          </span>
                        </>
                      ) : (
                        <span className="text-secondary">never</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary">Signup source</p>
                    {signupSource.isEmpty ? (
                      <p className="text-sm text-secondary">unknown</p>
                    ) : (
                      <div className="space-y-0.5">
                        {signupSource.surface && (
                          <p className="text-sm">
                            <span className="text-secondary">Surface: </span>
                            {signupSource.surface}
                          </p>
                        )}
                        {signupSource.landing && (
                          <p className="text-sm">
                            <span className="text-secondary">Landing page: </span>
                            {signupSource.landing}
                          </p>
                        )}
                        {signupSource.from && (
                          <p className="text-sm break-all">
                            <span className="text-secondary">Referrer: </span>
                            {signupSource.from}
                          </p>
                        )}
                        {signupSource.utm && (
                          <p className="text-sm break-all">
                            <span className="text-secondary">UTM: </span>
                            {signupSource.utm}
                          </p>
                        )}
                        {signupSource.ad && (
                          <p className="text-sm">
                            <span className="text-secondary">Google Ads click: </span>
                            Yes
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Teams Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Team Memberships</h2>
              
              {teams.length === 0 ? (
                <p className="text-secondary text-sm">Not a member of any teams</p>
              ) : (
                <div className="space-y-3">
                  {teams.map((team) => (
                    <div key={team.public_id} className="flex items-center justify-between p-3 bg-background rounded-md">
                      <div className="flex items-center space-x-3">
                        <UserGroupIcon className="h-5 w-5 text-secondary" />
                        <div>
                          <p className="font-medium">{team.name}</p>
                          <p className="text-sm text-secondary">Role: {team.role}</p>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/teams/${team.public_id}`)}
                      >
                        View Team
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {welcomeOffer && (
              <div className="bg-surface rounded-lg p-6">
                <h2 className="text-lg font-semibold flex items-center gap-2"><GiftIcon className="h-5 w-5 text-secondary" />Welcome offer</h2>
                <div className="mt-4"><WelcomeOfferStatus stage={welcomeOffer.stage} /></div>
                {!["shown", "dismissed"].includes(welcomeOffer.stage) && <Link to={`/offers/diagrams/${userId}`} className="mt-4 inline-block text-sm font-medium text-blue-600 hover:underline">View offer request →</Link>}
              </div>
            )}

            <ReferringAffiliateCard
              userId={userId!}
              userName={user.name}
              referringAffiliate={data.referring_affiliate}
            />

            {/* Feature Flags */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Feature Flags</h2>
                {updateFeatureFlag.isPending && (
                  <span className="text-xs text-secondary">Saving...</span>
                )}
              </div>
              {featureFlags.length === 0 ? (
                <p className="text-sm text-secondary">No feature flags are defined yet.</p>
              ) : (
                <div className="space-y-5">
                  {featureFlags.map((flag) => (
                    <div key={flag.key} className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium">{flag.name}</p>
                        {flag.description && (
                          <p className="text-sm text-secondary mt-1">{flag.description}</p>
                        )}
                        <p className="text-xs text-secondary mt-1">
                          {flag.source === "user"
                            ? `User override ${flag.enabled ? "enabled" : "disabled"} (Defaults to ${flag.defaultValue ? "On" : "Off"})`
                            : `Defaults to ${flag.defaultValue ? "On" : "Off"}`}
                        </p>
                      </div>
                      <Switch
                        checked={flag.enabled}
                        onCheckedChange={(checked) => handleFeatureFlagToggle(flag.key, checked)}
                        disabled={updateFeatureFlag.isPending}
                        aria-label={`Toggle ${flag.name}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Individual Usage Dashboard - Only show for users without teams */}
            {teams.length === 0 && individualLimitsData && (
              <div className="bg-surface rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Individual Usage Dashboard</h2>
                
                {/* Storage Progress Bar */}
                <div className="mb-6">
                  <StorageProgressBar
                    usedGB={individualLimitsData.usage.storage.used_gb}
                    limitGB={individualLimitsData.usage.storage.limit_gb}
                    percentage={individualLimitsData.usage.storage.percentage}
                  />
                </div>

                {/* Usage Chart */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-secondary">Resource Usage Overview</h3>
                  <UsageChart
                    data={[
                      {
                        name: "Games",
                        value: individualLimitsData.usage.games.count,
                        limit: individualLimitsData.usage.games.limit,
                        percentage: individualLimitsData.usage.games.percentage,
                      },
                    ]}
                  />
                </div>

                {/* Usage Alerts */}
                {(individualLimitsData.usage.storage.percentage >= 90 ||
                  individualLimitsData.usage.games.percentage >= 90) && (
                  <Alert variant="destructive" className="mt-4">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <div>
                      <p className="font-medium">High Usage Alert</p>
                      <p className="text-sm mt-1">
                        One or more resources are approaching their limits. The user may need to upgrade their plan.
                      </p>
                    </div>
                  </Alert>
                )}
              </div>
            )}

            {/* Anomalies Card - Only show if user has anomalies */}
            {teams.length === 0 && individualLimitsData?.anomalies && individualLimitsData.anomalies.length > 0 && (
              <div className="bg-surface rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Usage Anomalies</h2>
                
                <div className="space-y-3">
                  {individualLimitsData.anomalies.map((anomaly, index) => {
                    const severityColors = {
                      low: "bg-blue-50 text-blue-800 border-blue-200",
                      medium: "bg-amber-50 text-amber-800 border-amber-200",
                      high: "bg-red-50 text-red-800 border-red-200",
                    }
                    
                    const severityIcons = {
                      low: "ℹ️",
                      medium: "⚠️",
                      high: "🚨",
                    }

                    return (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg border ${severityColors[anomaly.severity]}`}
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-xl mt-0.5">{severityIcons[anomaly.severity]}</span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium capitalize">
                                {anomaly.type.replace(/_/g, " ")}
                              </h4>
                              <span className="text-xs">
                                {formatDistanceToNow(new Date(anomaly.timestamp), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm mt-1">{anomaly.message}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs">
                              <span>Metric: {anomaly.metric}</span>
                              <span>Value: {anomaly.value}</span>
                              <span>Threshold: {anomaly.threshold}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Activity Stats</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <UserGroupIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Teams</span>
                  </div>
                  <span className="font-medium">{stats.teamCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <VideoCameraIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Games</span>
                  </div>
                  <span className="font-medium">
                    {stats.gameCount}
                    {teams.length === 0 && individualLimitsData && (
                      <span className="text-secondary text-sm"> / {individualLimitsData.limits.max_games}</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FilmIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Clips</span>
                  </div>
                  <span className="font-medium">
                    {stats.clipCount}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Squares2X2Icon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Diagrams</span>
                  </div>
                  {(stats.diagramCount ?? 0) > 0 ? (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/diagrams?creator=${encodeURIComponent(user.public_id)}&creatorName=${encodeURIComponent(user.name)}`
                        )
                      }
                      className="font-medium text-primary hover:underline"
                    >
                      {stats.diagramCount}
                    </button>
                  ) : (
                    <span className="font-medium">0</span>
                  )}
                </div>
                {teams.length === 0 && individualLimitsData && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ServerIcon className="h-5 w-5 text-secondary" />
                      <span className="text-sm">Storage</span>
                    </div>
                    <span className="font-medium">
                      {individualLimitsData.usage.storage.used_gb} GB
                      <span className="text-secondary text-sm"> / {individualLimitsData.limits.storage_gb} GB</span>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Account Actions</h2>
              
              <div className="space-y-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => setShowRevokeSessionsDialog(true)}
                >
                  <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
                  Revoke All Sessions
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => navigate(`/users/${userId}/activity`)}
                >
                  <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                  View Activity Log
                </Button>
              </div>
            </div>

            {/* Danger Zone */}
            {canDelete && (
              <div className="bg-surface rounded-lg p-6 border border-destructive/30">
                <h2 className="text-lg font-semibold mb-1 text-destructive">Danger Zone</h2>
                <p className="text-sm text-secondary mb-4">
                  Permanently delete this user. Their team content is reassigned to
                  &ldquo;Deleted User&rdquo;; their personal content and account are erased.
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full justify-start"
                  onClick={openDeleteDialog}
                >
                  <TrashIcon className="h-3.5 w-3.5 mr-1" />
                  Delete user
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Suspend Dialog */}
      <Dialog
        open={showSuspendDialog}
        onOpenChange={setShowSuspendDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
            <DialogDescription>Suspend this user's access to the platform. You can set a temporary or permanent suspension.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Reason for suspension</label>
            <Input
              value={suspendForm.reason}
              onChange={(e) => setSuspendForm({ ...suspendForm, reason: e.target.value })}
              placeholder="Enter reason..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Duration</label>
            <Select
              value={suspendForm.duration}
              onValueChange={(value) => setSuspendForm({ ...suspendForm, duration: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
                <SelectItem value="permanent">Permanent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {suspendForm.duration === "custom" && (
            <div>
              <label className="block text-sm font-medium mb-1">Number of days</label>
              <Input
                type="number"
                value={suspendForm.customDays}
                onChange={(e) => setSuspendForm({ ...suspendForm, customDays: e.target.value })}
                min="1"
              />
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setShowSuspendDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleSuspend}
              disabled={!suspendForm.reason}
            >
              Suspend User
            </Button>
          </div>
        </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update the user's information.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <Input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <Input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_superuser"
              checked={editForm.is_superuser}
              onChange={(e) => setEditForm({ ...editForm, is_superuser: e.target.checked })}
              className="rounded border-border"
            />
            <label htmlFor="is_superuser" className="text-sm font-medium">
              Admin privileges
            </label>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>
              Save Changes
            </Button>
          </div>
        </div>
        </DialogContent>
      </Dialog>

      {/* Revoke Sessions Confirmation */}
      <ConfirmDialog
        open={showRevokeSessionsDialog}
        onOpenChange={setShowRevokeSessionsDialog}
        title="Revoke All Sessions"
        description="This will log the user out of all devices and require them to sign in again. Continue?"
        onConfirm={handleRevokeSessions}
      />

      {/* Delete User (type-to-confirm) */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open)
          if (!open) {
            setDeleteConfirmText("")
            setDeleteError(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-destructive">
              <TrashIcon className="h-5 w-5" />
              <span>Delete user</span>
            </DialogTitle>
            <DialogDescription>
              This permanently deletes {user.name} ({user.email}). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-destructive">This is permanent</p>
              <ul className="text-sm text-destructive/90 list-disc list-inside space-y-1">
                <li>
                  Team content stays put — it is <span className="font-medium">reassigned to
                  &ldquo;Deleted User&rdquo;</span>, not destroyed.
                </li>
                <li>The user&rsquo;s personal content and their account are erased.</li>
              </ul>
            </div>

            {deleteError && (
              <Alert variant="destructive">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <div>
                  <p className="font-medium">Couldn&rsquo;t delete this user</p>
                  <p className="text-sm mt-1">{deleteError}</p>
                </div>
              </Alert>
            )}

            <div>
              <label htmlFor="delete-confirm" className="block text-sm font-medium mb-1">
                Type <span className="font-mono">DELETE</span> to confirm
              </label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
                autoComplete="off"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteConfirmText !== "DELETE" || deleteUser.isPending}
              >
                {deleteUser.isPending ? "Deleting..." : "Delete user"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}